// Authentication Provider with MSAL
import React, { createContext, useContext, useState, useEffect } from "react";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { PublicClientApplication, InteractionStatus } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "../authConfig";
import { Loader2, LogIn, User, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Auth Context for additional state
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Login Page Component
const LoginPage = () => {
  const { instance, inProgress } = useMsal();
  const [error, setError] = useState(null);

  const handleLogin = async () => {
    try {
      setError(null);
      // Check if an interaction is already in progress
      if (inProgress !== InteractionStatus.None) {
        return;
      }
      await instance.loginRedirect(loginRequest);
    } catch (e) {
      console.error("Login failed:", e);
      // Handle interaction_in_progress by clearing stale state
      if (e.errorCode === "interaction_in_progress") {
        const keys = Object.keys(sessionStorage);
        keys.forEach((key) => {
          if (key.startsWith("msal.") && key.includes("interaction")) {
            sessionStorage.removeItem(key);
          }
        });
        setError("Bitte versuchen Sie es erneut.");
      } else {
        setError(e.message || "Login fehlgeschlagen");
      }
    }
  };

  const isLoading = inProgress !== InteractionStatus.None;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B4965] to-[#0D3A4D] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto">
            <img
              src="/logo.png"
              alt="DomusVita Logo"
              className="h-16 w-auto mx-auto"
            />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-[#1B4965]">
              DomusVita Korrekturrechnung
            </CardTitle>
            <CardDescription className="text-slate-600">
              Melden Sie sich mit Ihrem Microsoft-Konto an
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <Button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full bg-[#1B4965] hover:bg-[#163D54] text-white py-6 text-lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Anmeldung...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5 mr-2"
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="10" height="10" fill="#F25022" />
                  <rect x="11" width="10" height="10" fill="#7FBA00" />
                  <rect y="11" width="10" height="10" fill="#00A4EF" />
                  <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
                </svg>
                Mit Microsoft anmelden
              </>
            )}
          </Button>

          <div className="text-center text-xs text-slate-500 mt-4">
            <p>Nur autorisierte Mitarbeiter haben Zugang.</p>
            <p className="mt-1">Bei Problemen wenden Sie sich an die IT.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// User Menu Component
export const UserMenu = () => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  if (!account) return null;

  const initials = account.name
    ? account.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "U";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-9 w-9 rounded-full">
          <Avatar className="h-9 w-9 bg-white/20 text-white">
            <AvatarFallback className="bg-white/20 text-white text-sm">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{account.name}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {account.username}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
          <LogOut className="w-4 h-4 mr-2" />
          Abmelden
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Protected Route Wrapper
const ProtectedContent = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  // Show loading while authentication is in progress
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#1B4965] animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Authentifizierung...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Show protected content
  return children;
};

// Funktion zum Abrufen des Access Tokens für Microsoft Graph API
export const getAccessToken = async () => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    console.warn("No accounts found, user not logged in");
    return null;
  }

  const request = {
    scopes: ["User.Read", "Mail.Send", "Sites.ReadWrite.All"],
    account: accounts[0]
  };

  try {
    const response = await msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    console.error("Token acquisition failed:", error);
    // Falls silent fehlschlägt, versuche interaktiv
    try {
      const response = await msalInstance.acquireTokenPopup(request);
      return response.accessToken;
    } catch (popupError) {
      console.error("Interactive token acquisition failed:", popupError);
      return null;
    }
  }
};

// ID Token for our own backend API (audience = client_id, validated by EntraIDValidator)
export const getIdToken = async () => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: ["User.Read"],
      account: accounts[0],
    });
    return response.idToken;
  } catch (error) {
    try {
      const response = await msalInstance.acquireTokenPopup({
        scopes: ["User.Read"],
        account: accounts[0],
      });
      return response.idToken;
    } catch {
      return null;
    }
  }
};

// Auth Provider Wrapper Component
export const AuthProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initMsal = async () => {
      try {
        await msalInstance.initialize();
        // Handle redirect promise (for login redirect flow)
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          console.log("Login successful:", response.account?.name);
        }
      } catch (error) {
        console.error("MSAL initialization error:", error);
        // Clear stale interaction state from sessionStorage
        const keys = Object.keys(sessionStorage);
        keys.forEach((key) => {
          if (key.startsWith("msal.") && key.includes("interaction")) {
            sessionStorage.removeItem(key);
          }
        });
      } finally {
        setIsInitialized(true);
      }
    };
    initMsal();
  }, []);

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#1B4965] animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Lade Anwendung...</p>
        </div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthContext.Provider value={{ msalInstance, getAccessToken, getIdToken }}>
        <ProtectedContent>{children}</ProtectedContent>
      </AuthContext.Provider>
    </MsalProvider>
  );
};

export default AuthProvider;
